import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../axios';

const initialState = {
    riderApprovalSettings: {
        loading: false,
        data: null,
        error: null,
    },
};

export const getRiderApprovalSettings = createAsyncThunk(
    'RiderApprovalSettings/getRiderApprovalSettings',
    async (corporateId, { rejectWithValue }) => {
        try {
            // If corporate ID is provided, fetch corporate-specific settings
            const endpoint = corporateId 
                ? `settings/rider-approval/corporate/${corporateId}`
                : 'settings/rider-approval';
            
            const response = await axios.get(endpoint);
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.response?.statusText ||
                error.message ||
                'Unknown error occurred';
            const statusCode = error.response?.status;
            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
            });
        }
    },
);

export const updateRiderApprovalSettings = createAsyncThunk(
    'RiderApprovalSettings/updateRiderApprovalSettings',
    async (settingsData, { rejectWithValue }) => {
        try {
            // Extract corporate ID if present
            const { corporateId, ...settings } = settingsData;
            
            // Choose endpoint based on whether corporate ID is provided
            const endpoint = corporateId 
                ? `settings/rider-approval/corporate/${corporateId}`
                : 'settings/rider-approval';
            
            const response = await axios.put(endpoint, settings);
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.response?.statusText ||
                error.message ||
                'Unknown error occurred';
            const statusCode = error.response?.status;
            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
            });
        }
    },
);

const riderApprovalSettingsSlice = createSlice({
    name: 'riderApprovalSettings',
    initialState,
    reducers: {
        resetRiderApprovalSettings: (state) => {
            state.riderApprovalSettings = initialState.riderApprovalSettings;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get rider approval settings
            .addCase(getRiderApprovalSettings.pending, (state) => {
                state.riderApprovalSettings.loading = true;
                state.riderApprovalSettings.error = null;
            })
            .addCase(getRiderApprovalSettings.fulfilled, (state, action) => {
                state.riderApprovalSettings.loading = false;
                state.riderApprovalSettings.data = action.payload;
                state.riderApprovalSettings.error = null;
            })
            .addCase(getRiderApprovalSettings.rejected, (state, action) => {
                state.riderApprovalSettings.loading = false;
                state.riderApprovalSettings.error =
                    action.payload?.message || action.error?.message || 'Unknown error';
            })
            // Update rider approval settings
            .addCase(updateRiderApprovalSettings.pending, (state) => {
                state.riderApprovalSettings.loading = true;
                state.riderApprovalSettings.error = null;
            })
            .addCase(updateRiderApprovalSettings.fulfilled, (state, action) => {
                state.riderApprovalSettings.loading = false;
                state.riderApprovalSettings.data = action.payload;
                state.riderApprovalSettings.error = null;
            })
            .addCase(updateRiderApprovalSettings.rejected, (state, action) => {
                state.riderApprovalSettings.loading = false;
                state.riderApprovalSettings.error =
                    action.payload?.message || action.error?.message || 'Unknown error';
            });
    },
});

export default riderApprovalSettingsSlice.reducer;
export const { resetRiderApprovalSettings } = riderApprovalSettingsSlice.actions;
